import { useMemo } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

const STORAGE_KEY = "somethings-support-chat-favorites";

interface SupportChatFavoritesStore {
  /** User the favorites belong to, so a different account isn't affected. */
  userId: string | null;
  /** Favorited internal-ops Sendbird channel URLs for the current user. */
  channelUrls: string[];
  toggle: (userId: string | null, channelUrl: string) => void;
  /** Remove a channel from favorites, e.g. once it is confirmed deleted. */
  prune: (channelUrl: string) => void;
}

/**
 * Resolves the favorite list for a given user, treating a mismatched owner as
 * an empty list so switching accounts in the same browser starts clean.
 */
const listForUser = (
  state: Pick<SupportChatFavoritesStore, "userId" | "channelUrls">,
  userId: string | null,
): string[] => (state.userId === userId ? state.channelUrls : []);

export const useSupportChatFavoritesStore = create<SupportChatFavoritesStore>()(
  persist(
    (set, get) => ({
      userId: null,
      channelUrls: [],
      toggle: (userId, channelUrl) => {
        const list = listForUser(get(), userId);
        set({
          userId,
          channelUrls: list.includes(channelUrl)
            ? list.filter((url) => url !== channelUrl)
            : [...list, channelUrl],
        });
      },
      prune: (channelUrl) =>
        set((state) => ({
          channelUrls: state.channelUrls.filter((url) => url !== channelUrl),
        })),
    }),
    { name: STORAGE_KEY },
  ),
);

/**
 * Reactive set of favorited channel URLs for the given user. Returns a stable
 * `Set` for O(1) membership checks while bucketing the support channel list.
 */
export function useFavoriteChannelUrls(userId?: string | null): Set<string> {
  const storeUserId = useSupportChatFavoritesStore((s) => s.userId);
  const channelUrls = useSupportChatFavoritesStore((s) => s.channelUrls);
  const resolvedUserId = userId ?? null;
  return useMemo(
    () => new Set(listForUser({ userId: storeUserId, channelUrls }, resolvedUserId)),
    [storeUserId, channelUrls, resolvedUserId],
  );
}

/** Reactive check for whether a single channel is favorited by the user. */
export function useIsChannelFavorited(
  userId: string | null | undefined,
  channelUrl: string | null | undefined,
): boolean {
  const storeUserId = useSupportChatFavoritesStore((s) => s.userId);
  const channelUrls = useSupportChatFavoritesStore((s) => s.channelUrls);
  if (!channelUrl) return false;
  return listForUser(
    { userId: storeUserId, channelUrls },
    userId ?? null,
  ).includes(channelUrl);
}

// Keep tabs in sync: when another tab favorites/unfavorites, rehydrate this
// store so the star + Favorites section reflect the change immediately.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) {
      void useSupportChatFavoritesStore.persist.rehydrate();
    }
  });
}
